import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, UserRound, Trash2, MessageCircle, CalendarDays, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import { deleteOrder, getBusiness, listOrders, updateOrderStatus } from "@/lib/api";
import { dayjs, money, ORDER_STATUS } from "@/lib/format";

const FILTERS = [["", "Todos"], ...Object.entries(ORDER_STATUS).map(([k, v]) => [k, v.label])];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [cur, setCur] = useState("USD");

  const load = () => listOrders(status ? { status } : {}).then(setOrders).catch(() => toast.error("No se pudieron cargar los pedidos"));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBusiness().then((b) => setCur(b.currency || "USD")).catch(() => {}); }, []);

  const change = async (o, s) => { await updateOrderStatus(o.id, s); toast.success(`Pedido ${ORDER_STATUS[s].label.toLowerCase()}`); load(); };
  const remove = async (o) => { if (!window.confirm("¿Eliminar este registro?")) return; await deleteOrder(o.id); load(); };

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="orders-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <PageHeader title="Pedidos y reservas" subtitle="El bot registra aquí automáticamente cada pedido o reserva que el cliente confirma por WhatsApp. Tus asesores también pueden crearlos desde el chat." />
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map(([k, label]) => (
            <button key={k || "all"} data-testid={`orders-filter-${k || "all"}`} onClick={() => setStatus(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${status === k ? "bg-[#111B21] text-white border-[#111B21]" : "bg-white border-slate-200 hover:border-slate-400"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {orders.length === 0 ? (
            <div className="p-14 text-center" data-testid="orders-empty">
              <Package className="h-8 w-8 mx-auto text-slate-300" />
              <p className="mt-3 font-display text-lg font-bold">Sin pedidos todavía</p>
              <p className="text-sm text-slate-500 mt-1">Prueba en el <Link to="/simulador" className="text-emerald-600 font-semibold">simulador</Link>: pide algo y confírmalo.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead>Detalle</TableHead>
                  <TableHead className="text-right">Total</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell>
                      <p className="font-semibold">{o.contact_name || "Cliente"}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        {o.source === "bot" ? <><Bot className="h-3 w-3" />Bot</> : <><UserRound className="h-3 w-3" />{o.created_by || "Asesor"}</>}
                      </p>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100">
                        {o.type === "reserva" ? <CalendarDays className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                        {o.type === "reserva" ? "Reserva" : "Pedido"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {o.items?.map((i) => <p key={i.name} className="text-sm">{i.qty} × {i.name}</p>)}
                      {o.notes && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{o.notes}</p>}
                    </TableCell>
                    <TableCell className="text-right font-bold" data-testid={`order-total-${o.id}`}>{o.items?.length || o.total ? money(o.total, cur) : "—"}{o.delivery_fee > 0 && <p className="text-[11px] font-normal text-slate-400">incl. envío {money(o.delivery_fee, cur)}</p>}</TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => change(o, v)}>
                        <SelectTrigger data-testid={`order-status-${o.id}`} className={`h-8 w-36 rounded-full border text-xs font-semibold ${ORDER_STATUS[o.status]?.cls}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(ORDER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{dayjs(o.created_at).format("D MMM, HH:mm")}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {o.conversation_id && (
                        <Link to={`/chats?c=${o.conversation_id}`}><Button size="icon" variant="ghost" data-testid={`order-open-chat-${o.id}`}><MessageCircle className="h-4 w-4" /></Button></Link>
                      )}
                      <Button size="icon" variant="ghost" data-testid={`order-delete-${o.id}`} onClick={() => remove(o)} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
